import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Box, Button, TextField } from '@mui/material'
import { useMutation } from '@apollo/react-hooks'
import gql from 'graphql-tag'
import { useAuthentication } from '../hooks/useAuthentication'

const REQUEST_OTP = gql`
  mutation requestOtp($input: RequestOtpInput!) {
    requestOtp(input: $input)
  }
`

const VERIFY_OTP = gql`
  mutation verifyOtp($input: VerifyOtpInput!) {
    verifyOtp(input: $input) {
      token
      user {
        first_name
        last_name
        email
        branch
        site_admin
        user_role {
          id
          name
        }
        department {
          id
          name
        }
      }
    }
  }
`

type Step = 'request' | 'verify'

const OtpLoginForm = () => {
  const { signIn } = useAuthentication()
  const [step, setStep] = useState<Step>('request')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [uiError, setUiError] = useState('')

  const [requestOtp, requestResp] = useMutation(REQUEST_OTP)
  const [verifyOtp, verifyResp] = useMutation(VERIFY_OTP)

  const loading = requestResp.loading || verifyResp.loading
  const errorMessage = useMemo(() => uiError || '', [uiError])

  const clearErrors = () => {
    setUiError('')
    // Some Apollo versions expose `reset()` on mutation results; call it when available.
    ;(requestResp as any)?.reset?.()
    ;(verifyResp as any)?.reset?.()
  }

  useEffect(() => {
    if (requestResp.data?.requestOtp) {
      setStep('verify')
    }
  }, [requestResp.data])

  // In case errors are reported via the hook result instead of thrown from mutate().
  useEffect(() => {
    if (requestResp.error) {
      const msg: string =
        (requestResp.error as any)?.graphQLErrors?.[0]?.message ||
        (requestResp.error as any)?.networkError?.message ||
        requestResp.error.message ||
        'Something went wrong. Please try again.'
      setUiError((prev) => (prev === msg ? prev : msg))
    }
  }, [requestResp.error])

  useEffect(() => {
    if (verifyResp.error) {
      const msg: string =
        (verifyResp.error as any)?.graphQLErrors?.[0]?.message ||
        (verifyResp.error as any)?.networkError?.message ||
        verifyResp.error.message ||
        'Something went wrong. Please try again.'
      setUiError((prev) => (prev === msg ? prev : msg))
    }
  }, [verifyResp.error])

  useEffect(() => {
    if (verifyResp.data?.verifyOtp?.token) {
      signIn(verifyResp.data.verifyOtp.token)
    }
  }, [verifyResp.data, signIn])

  const handleMutationError = (err: any) => {
    const msg: string =
      err?.graphQLErrors?.[0]?.message ||
      err?.networkError?.message ||
      err?.message ||
      'Something went wrong. Please try again.'

    setUiError(msg)

    // Friendly UX for expired OTP: clear the code and let user resend/request again.
    if (String(msg).toLowerCase().includes('otp has expired')) {
      setCode('')
    }
  }

  const onRequest = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!email) return
    clearErrors()
    try {
      await requestOtp({ variables: { input: { email } } })
    } catch (err) {
      handleMutationError(err)
    }
  }

  const onVerify = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!email || !code) return
    clearErrors()
    try {
      await verifyOtp({ variables: { input: { email, code } } })
    } catch (err) {
      handleMutationError(err)
    }
  }

  return (
    <Box>
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {errorMessage}
        </Alert>
      )}

      {step === 'request' ? (
        <form onSubmit={onRequest}>
          <TextField
            id="otp-email"
            label="Email"
            type="email"
            value={email}
            onChange={(e) => {
              clearErrors()
              setEmail(e.currentTarget.value)
            }}
            variant="filled"
            margin="normal"
            fullWidth
            required
          />
          <Box display="flex" justifyContent="flex-end">
            <Button
              type="submit"
              variant="contained"
              size="small"
              color="primary"
              disabled={loading}
            >
              Send Code
            </Button>
          </Box>
        </form>
      ) : (
        <form onSubmit={onVerify}>
          <TextField
            id="otp-email-readonly"
            label="Email"
            type="email"
            value={email}
            variant="filled"
            margin="normal"
            fullWidth
            disabled
          />
          <TextField
            id="otp-code"
            label="One-time password"
            type="text"
            value={code}
            onChange={(e) => setCode(e.currentTarget.value)}
            variant="filled"
            margin="normal"
            fullWidth
            required
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            autoFocus
          />
          <Box display="flex" justifyContent="space-between">
            <Button
              type="button"
              size="small"
              disabled={loading}
              onClick={() => {
                clearErrors()
                setCode('')
                setStep('request')
              }}
            >
              Change email
            </Button>
            <Box>
              <Button
                type="button"
                size="small"
                disabled={loading}
                onClick={async () => {
                  clearErrors()
                  try {
                    await requestOtp({ variables: { input: { email } } })
                  } catch (err) {
                    handleMutationError(err)
                  }
                }}
              >
                Resend
              </Button>
              <Button
                type="submit"
                variant="contained"
                size="small"
                color="primary"
                disabled={loading}
                sx={{ ml: 1 }}
              >
                Verify
              </Button>
            </Box>
          </Box>
        </form>
      )}
    </Box>
  )
}

export default OtpLoginForm
